import './DepositHistory.css';
import { useEffect, useRef, useState } from 'react';

const ITEMS_PER_PAGE = 15;

function buildPageNumbers(current, total) {
  if (total <= 1) return [];

  const pages = [];
  const rangeStart = Math.max(2, current - 1);
  const rangeEnd = Math.min(total - 1, current + 1);

  pages.push(1);
  if (rangeStart > 2) pages.push('…');
  for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
  if (rangeEnd < total - 1) pages.push('…');
  if (total > 1) pages.push(total);

  return pages;
}

function DepositHistory({ onBack }) {
  const [depositHistory, setDepositHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const listRef = useRef(null);

  useEffect(() => {
    const fetchDepositHistory = async () => {
      try {
        setLoading(true);
        const user = JSON.parse(localStorage.getItem('user'));

        if (!user || !user.id) {
          setError('User not found. Please login again.');
          return;
        }

        const response = await fetch(
          `https://backend.mainbazar777.site/api/deposit_history/${user.id}`
        );

        if (!response.ok) throw new Error('Failed to fetch deposit history');

        const data = await response.json();
        setDepositHistory(data.success && Array.isArray(data.data) ? data.data : []);
      } catch (err) {
        console.error('Error fetching deposit history:', err);
        setError('Failed to load deposit history. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchDepositHistory();
  }, []);

  const totalPages = Math.ceil(depositHistory.length / ITEMS_PER_PAGE);
  const pageItems = depositHistory.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );
  const pageNumbers = buildPageNumbers(currentPage, totalPages);

  const goToPage = (page) => {
    setCurrentPage(page);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 0:  return 'badge-rejected';
      case 1:  return 'badge-pending';
      case 2: return 'badge-approved';
      default: return 'badge-pending';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 0:  return 'REJECTED';
      case 1:  return 'PENDING';
      case 2: return 'APPROVED';
      default: return 'PENDING';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  return (
    <div className="dh-page" ref={listRef}>
      <div className="dh-header">
        <button className="dh-back-btn" onClick={onBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M15 18L9 12L15 6" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        <h1 className="dh-title">Add Fund History</h1>
        <div style={{ width: 40 }} />
      </div>

      {loading ? (
        <div className="dh-center">
          <div className="dh-spinner" />
          <p className="dh-hint">Loading...</p>
        </div>
      ) : error ? (
        <div className="dh-center">
          <p className="dh-error-text">{error}</p>
        </div>
      ) : depositHistory.length === 0 ? (
        <div className="dh-center">
          <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#cbd5e0" strokeWidth="1.5">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4m0 4h.01" strokeLinecap="round" />
          </svg>
          <p className="dh-hint">No deposit history found</p>
        </div>
      ) : (
        <>
          <div className="dh-list">
            {pageItems.map((deposit) => (
              <div key={deposit.id} className="dh-card">
                <div className="dh-icon-wrap">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M19 12l-7 7-7-7" />
                  </svg>
                </div>

                <div className="dh-mid">
                  <span className="dh-label">Add Funds</span>
                  <span className="dh-date">{formatDate(deposit.amount_requested_at)}</span>
                  <span className="dh-order">#{deposit.order_id? deposit.order_id : deposit.utr_number}</span>
                </div>

                <div className="dh-right">
                  <span className="dh-amount">+₹{parseFloat(deposit.amount).toFixed(0)}</span>
                  <span className={`dh-badge ${getStatusClass(deposit.status)}`}>
                    {getStatusText(deposit.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div className="dh-pagination">
              <button
                className="dh-pg-btn dh-pg-nav"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M15 18L9 12L15 6" />
                </svg>
              </button>

              {pageNumbers.map((pg, idx) =>
                pg === '…' ? (
                  <span key={`ellipsis-${idx}`} className="dh-pg-ellipsis">…</span>
                ) : (
                  <button
                    key={pg}
                    className={`dh-pg-btn${currentPage === pg ? ' dh-pg-active' : ''}`}
                    onClick={() => goToPage(pg)}
                  >
                    {pg}
                  </button>
                )
              )}

              <button
                className="dh-pg-btn dh-pg-nav"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18L15 12L9 6" />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default DepositHistory;
